import { VariantUpdatedEvent, VariantSelectedEvent } from '@theme/events';
// import { morph } from '@theme/morph';

class VariantPicker extends HTMLElement {
  // Contrôleur pour annuler les requêtes en cours
  #abortController;

  // Picker au niveau section (hors product-form) : même section Shopify que le formulaire.
  #getProductForm() {
    const nestedForm = this.closest('product-form');
    if (nestedForm) return nestedForm;

    const section = this.closest('.shopify-section');
    if (!section) return null;

    const productId = this.dataset.productId;
    if (productId) {
      return section.querySelector(`product-form[data-product-id="${CSS.escape(String(productId))}"]`);
    }

    return section.querySelector('product-form');
  }

  connectedCallback() {
    this.addEventListener('change', this.variantChanged.bind(this));
    this.applyPickerScope();
    this.syncInitialSelection();
    this.#forceCheckedStateOnLoad();
  }

  // Force le CSS :checked à s'appliquer au chargement de la page
  #forceCheckedStateOnLoad() {
    requestAnimationFrame(() => {
      const checkedInputs = this.querySelectorAll('input[type="radio"]:checked, input[type="radio"][checked]');
      for (const input of checkedInputs) {
        input.checked = true;
      }
    });
  }

  /**
   * Gère l'événement de changement de variante.
   * @param {Event} event - L'événement de changement de variante.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Met à jour l'option sélectionnée dans l'interface
    this.updateSelectedOption(event.target);
    this.syncSelectedOptionAcrossPickers(event.target.dataset.optionValueId ?? null);
    this.updateStickySummary();

    // Déclenche un événement personnalisé pour notifier la sélection
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '', value: event.target.value ?? '' }));

    // Lance la requête pour mettre à jour la section
    this.fetchUpdatedSection(this.buildRequestUrl());

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    this.updateUrl(event.target);
  }

  // Met à jour l'option sélectionnée dans l'interface.
  updateSelectedOption(target) {
    // Si c'est une chaîne, trouve l'élément correspondant pour récupérer l'ID de la valeur d'option
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    // Met à jour l'état "checked" pour les boutons radio
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      // Désélectionne tous les autres radios du même groupe (name)
      const radioGroup = this.querySelectorAll(`input[type="radio"][name="${target.name}"]`);
      for (const radio of radioGroup) {
        radio.checked = false;
        radio.removeAttribute('checked');
        radio.removeAttribute('aria-checked');
      }
      
      // Sélectionne le nouveau radio
      target.checked = true;
      target.setAttribute('checked', 'checked');
      target.setAttribute('aria-checked', 'true');
      
      // Force le navigateur à recalculer le style :checked
      requestAnimationFrame(() => {
        target.checked = true;
      });
    }

    // Met à jour l'attribut "selected" pour les éléments select
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      // Supprime l'attribut selected de toutes les options
      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      // Ajoute l'attribut selected à la nouvelle option
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  // Construit l'URL de requête pour récupérer les données de variante.
  buildRequestUrl() {
    const params = [`option_values=${this.selectedOptionsValues.join(',')}`];

    // Featured Product (landing, etc.) : re-rendre la section courante, pas la fiche produit complète.
    if (this.dataset.sectionId) {
      params.push(`section_id=${encodeURIComponent(this.dataset.sectionId)}`);
    }

    return `${this.dataset.productUrl}?${params.join('&')}`;
  }

  // Racine HTML de la réponse (section product-featured, main-product, etc.).
  #getResponseRoot(doc) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return doc;

    return (
      doc.getElementById(`shopify-section-${sectionId}`) ||
      doc.querySelector(`#shopify-section-${sectionId}`) ||
      doc.querySelector('.shopify-section') ||
      doc.body?.firstElementChild ||
      doc
    );
  }

  #getVariantPickerFromResponse(responseRoot) {
    const context = this.dataset.variantPickerContext;
    if (context) {
      return (
        responseRoot.querySelector(`variant-picker[data-variant-picker-context="${context}"]`) ||
        responseRoot.querySelector('variant-picker')
      );
    }

    return responseRoot.querySelector('variant-picker');
  }

  // getElementById n'existe que sur Document ; la réponse section_id est un Element.
  #findById(root, id) {
    if (!id || !root) return null;
    if (typeof root.getElementById === 'function') {
      return root.getElementById(id);
    }
    return root.querySelector(`#${CSS.escape(id)}`);
  }

  // Récupère la section mise à jour depuis le serveur.
  fetchUpdatedSection(requestUrl) {
    // Annule la requête fetch précédente si elle est encore en cours
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const separator = requestUrl.includes('?') ? '&' : '?';
    const urlWithBust = `${requestUrl}${separator}_fd=${Date.now()}`;

    fetch(urlWithBust, { signal: this.#abortController.signal, cache: 'no-store' })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const responseRoot = this.#getResponseRoot(html);
        const variantPickerSource = this.#getVariantPickerFromResponse(responseRoot);
        const textContent = variantPickerSource?.querySelector('script[type="application/json"]')?.textContent;

        if (!textContent) return;

        this.updateVariantPicker(responseRoot);
        this.syncOtherPickers(responseRoot);
        this.updateSourcesFromDestinations(responseRoot);

        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdatedEvent(JSON.parse(textContent), this.selectedOptionId, {
              html: responseRoot,
              productId: this.dataset.productId ?? '',
            }),
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log("Fetch interrompu par l'utilisateur");
        } else {
          console.error(error);
        }
      });
  }

  // Met à jour les blocs marqués data-should-update dans la même section Shopify uniquement.
  updateSourcesFromDestinations(newHtml) {
    const pickerSection = this.closest('.shopify-section');
    if (!pickerSection) return;

    const destinations = pickerSection.querySelectorAll('[data-should-update]');
    destinations.forEach((destination) => {
      const source = this.#findById(newHtml, destination.id);
      const shouldHide = destination.dataset.shouldHide;
      if (source) {
        destination.innerHTML = source.innerHTML;
        destination.classList.toggle('hidden', shouldHide === 'true');
      }
    });
  }

  // Synchronise les autres variant pickers du même product-form.
  syncOtherPickers(newHtml) {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const otherPickers = Array.from(productForm.querySelectorAll('variant-picker')).filter((picker) => picker !== this);
    if (otherPickers.length === 0) return;

    for (const picker of otherPickers) {
      picker.updateVariantPicker(newHtml);
    }
  }

  // Synchronise la sélection entre pickers sans relancer de requête.
  syncSelectedOptionAcrossPickers(optionValueId) {
    if (!optionValueId) return;

    const productForm = this.#getProductForm();
    if (!productForm) return;

    const otherPickers = Array.from(productForm.querySelectorAll('variant-picker')).filter((picker) => picker !== this);
    if (otherPickers.length === 0) return;

    for (const picker of otherPickers) {
      picker.updateSelectedOption(optionValueId);
    }
  }

  // Re-rend le sélecteur de variante avec les nouvelles données.
  updateVariantPicker(newHtml) {
    // Trouve le nouveau variant picker dans la réponse
    const context = this.dataset.variantPickerContext;
    const selector = context ? `variant-picker[data-variant-picker-context="${context}"]` : this.tagName.toLowerCase();
    const newVariantPickerSource = newHtml.querySelector(selector) || newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('Aucune nouvelle source de variant picker trouvée');
    }

    // Garde toutes les sélections avant le re-rend (pour produits multi-options)
    const previousSelectedOptionsIds = this.selectedOptionsValues;

    // Met à jour le variant picker
    this.innerHTML = newVariantPickerSource.innerHTML;
    this.applyPickerScope();

    // Restaure toutes les sélections (une par option)
    if (previousSelectedOptionsIds && previousSelectedOptionsIds.length > 0) {
      for (const optionValueId of previousSelectedOptionsIds) {
        this.updateSelectedOption(optionValueId);
      }
      // Synchronise uniquement la dernière option changée avec les autres pickers
      const lastChangedId = previousSelectedOptionsIds[previousSelectedOptionsIds.length - 1];
      if (lastChangedId) {
        this.syncSelectedOptionAcrossPickers(lastChangedId);
      }
    }

    this.updateStickySummary();
  }

  // Évite les collisions d'ID/for quand il y a plusieurs pickers (ex: sticky).
  applyPickerScope() {
    if (!this.closest('sticky-add-to-cart')) return;

    if (!this.dataset.pickerScope) {
      this.dataset.pickerScope = `sticky-${Math.random().toString(36).slice(2, 8)}`;
    }

    const scope = this.dataset.pickerScope;

    for (const input of this.querySelectorAll('input[id]')) {
      const originalId = input.dataset.originalId || input.id;
      input.dataset.originalId = originalId;
      input.id = `${originalId}--${scope}`;

      const originalName = input.dataset.originalName || input.name;
      input.dataset.originalName = originalName;
      input.name = `${originalName}--${scope}`;
    }

    for (const label of this.querySelectorAll('label[for]')) {
      const originalFor = label.dataset.originalFor || label.getAttribute('for');
      if (!originalFor) continue;
      label.dataset.originalFor = originalFor;
      label.setAttribute('for', `${originalFor}--${scope}`);
    }
  }

  // Aligne la sélection sticky -> main (ou inverse) au chargement.
  syncInitialSelection() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const isSticky = this.dataset.variantPickerContext === 'sticky' || this.closest('sticky-add-to-cart');
    if (!isSticky) return;

    const mainPicker = productForm.querySelector('variant-picker[data-variant-picker-context="main"]');
    if (!mainPicker) return;

    const mainSelected = mainPicker.selectedOptionId;
    if (mainSelected) {
      this.updateSelectedOption(mainSelected);
      this.updateStickySummary();
      return;
    }

    if (this.selectedOptionId) {
      this.syncSelectedOptionAcrossPickers(this.selectedOptionId);
      this.updateStickySummary();
    }
  }

  updateStickySummary() {
    const summary = this.#getProductForm()?.querySelector('[data-ref="sticky-variant-summary"]');
    const stickyContainer = summary?.closest('[data-sticky-variant-picker="false"]');
    if (!summary || !stickyContainer) return;

    const labelEl = summary.querySelector('.product-add-to-cart-button__variant-summary-label');
    const valuesEl = summary.querySelector('.product-add-to-cart-button__variant-summary-values');
    if (!labelEl || !valuesEl) return;

    const optionGroups = this.querySelectorAll('.variant-picker__option');
    if (optionGroups.length === 0) return;

    const firstOptionName = optionGroups[0].querySelector('.variant-picker__option-name span')?.textContent?.split(':')[0]?.trim();
    if (firstOptionName) {
      const labelBase = firstOptionName.split(' ')[0] || firstOptionName;
      labelEl.textContent = `${labelBase} :`;
    } else {
      labelEl.textContent = '';
    }

    const selectedValues = [];
    for (const group of optionGroups) {
      const selectedValue = group.querySelector('select option[selected]')?.textContent?.trim()
        || group.querySelector('input:checked')?.value?.trim()
        || group.querySelector('.variant-picker__selected-value')?.textContent?.trim();
      if (selectedValue) selectedValues.push(selectedValue);
    }

    valuesEl.textContent = selectedValues.join('-');
  }

  // Met à jour l'URL avec l'ID de variante sur les pages produit
  updateUrl(target) {
    // Détermine si nous sommes sur une page produit (pas dans une carte produit ou dialogue)
    const isOnProductPage =
      Theme.template.name === 'product' &&
      !target.closest('product-card') &&
      !target.closest('quick-add-dialog');

    // Prépare l'URL pour la mise à jour de l'historique du navigateur
    const url = new URL(window.location.href);

    let variantId;

    // Extrait l'ID de la variante selon le type d'élément (radio ou select)
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      variantId = target.dataset.variantId || null;
    } else if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Met à jour l'URL dans l'historique du navigateur si elle a changé
    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  // Récupère l'option actuellement sélectionnée.
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  // Récupère l'ID de l'option actuellement sélectionnée.
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("Aucun ID de valeur d'option trouvé");
    }

    return optionValueId;
  }

  // Récupère toutes les valeurs d'options actuellement sélectionnées.
  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("Aucun ID de valeur d'option trouvé");

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

